import {
  CHART_HEIGHT,
  CHART_PLOT,
  CHART_WIDTH,
  plotWidth,
  shortLabel,
  visibleXLabels,
  xFor,
  yFor,
  type ChartArtifact,
  type ChartModel,
  type SeriesLookup,
} from './chartModel.js';

const COLORS = [
  'var(--accent-1)',
  'var(--status-info-fg)',
  'var(--status-success-fg)',
  'var(--status-warn-fg)',
  'var(--status-error-fg)',
  'var(--fg-2)',
] as const;

const DASHES = ['', '6 3', '3 3', '8 4', '2 5', '1 4'] as const;

interface ChartSvgProps {
  artifact: ChartArtifact;
  model: ChartModel;
  titleId: string;
  format: (n: number) => string;
  tooltip: (series: string, x: string, y: number) => string;
}

export function ChartSvg({ artifact, model, titleId, format, tooltip }: ChartSvgProps) {
  return (
    <div
      style={{
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-1)',
        overflowX: 'auto',
      }}
    >
      <svg
        role="img"
        aria-labelledby={titleId}
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        style={{ display: 'block', width: '100%', minWidth: 'min(100%, 420px)' }}
      >
        <title id={titleId}>{artifact.title}</title>
        <ChartAxes labels={model.labels} min={model.min} max={model.max} format={format} />
        {artifact.chartType === 'line' ? (
          <LineChartBody {...model} tooltip={tooltip} />
        ) : artifact.chartType === 'stacked_bar' ? (
          <StackedBarChartBody {...model} tooltip={tooltip} />
        ) : (
          <BarChartBody {...model} tooltip={tooltip} />
        )}
      </svg>
    </div>
  );
}

export function ChartLegend({ series }: { series: string[] }) {
  if (series.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--s-3)' }}>
      {series.map((name, index) => (
        <span
          key={name}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--s-2)',
            color: 'var(--fg-2)',
            fontSize: 'var(--fz-12)',
          }}
        >
          <svg aria-hidden="true" viewBox="0 0 18 10" width="18" height="10">
            <rect
              x="1"
              y="2"
              width="16"
              height="6"
              fill={color(index)}
              stroke="var(--fg-2)"
              strokeDasharray={dash(index)}
            />
          </svg>
          {name}
        </span>
      ))}
    </div>
  );
}

function ChartAxes({
  labels,
  min,
  max,
  format,
}: {
  labels: string[];
  min: number;
  max: number;
  format: (n: number) => string;
}) {
  const ticks = [min, (min + max) / 2, max];
  const zero = yFor(0, min, max);
  return (
    <g>
      <line
        x1={CHART_PLOT.left}
        y1={CHART_PLOT.top}
        x2={CHART_PLOT.left}
        y2={CHART_HEIGHT - CHART_PLOT.bottom}
        stroke="var(--border-strong)"
      />
      <line
        x1={CHART_PLOT.left}
        y1={zero}
        x2={CHART_WIDTH - CHART_PLOT.right}
        y2={zero}
        stroke="var(--border-1)"
      />
      {ticks.map((tick) => (
        <Tick key={tick} value={tick} min={min} max={max} format={format} />
      ))}
      {visibleXLabels(labels).map(({ label, index }) => (
        <text
          key={`${label}-${index}`}
          x={xFor(index, labels.length)}
          y={CHART_HEIGHT - 18}
          textAnchor="middle"
          style={{ fill: 'var(--fg-3)', fontSize: 'var(--fz-11)' }}
        >
          <title>{label}</title>
          {shortLabel(label)}
        </text>
      ))}
    </g>
  );
}

function Tick({
  value,
  min,
  max,
  format,
}: {
  value: number;
  min: number;
  max: number;
  format: (n: number) => string;
}) {
  const y = yFor(value, min, max);
  return (
    <g>
      <line
        x1={CHART_PLOT.left}
        y1={y}
        x2={CHART_WIDTH - CHART_PLOT.right}
        y2={y}
        stroke="var(--border-2)"
      />
      <text
        x={CHART_PLOT.left - 8}
        y={y + 4}
        textAnchor="end"
        style={{
          fill: 'var(--fg-3)',
          fontSize: 'var(--fz-11)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {format(value)}
      </text>
    </g>
  );
}

function BarChartBody({
  labels,
  series,
  min,
  max,
  tooltip,
}: ChartModel & { tooltip: (series: string, x: string, y: number) => string }) {
  const groupWidth = plotWidth() / Math.max(1, labels.length);
  const barWidth = Math.max(2, (groupWidth * 0.68) / Math.max(1, series.length));
  const zero = yFor(0, min, max);
  return (
    <g>
      {labels.flatMap((label, labelIndex) =>
        series.map((s, seriesIndex) => {
          const value = s.values.get(label) ?? 0;
          const y = yFor(value, min, max);
          const x =
            CHART_PLOT.left +
            labelIndex * groupWidth +
            (groupWidth - barWidth * series.length) / 2 +
            seriesIndex * barWidth;
          return (
            <rect
              key={`${s.name}-${label}`}
              x={x}
              y={Math.min(y, zero)}
              width={barWidth * 0.82}
              height={Math.max(1, Math.abs(zero - y))}
              fill={color(seriesIndex)}
              stroke="var(--bg-1)"
              strokeDasharray={dash(seriesIndex)}
            >
              <title>{tooltip(s.name, label, value)}</title>
            </rect>
          );
        }),
      )}
    </g>
  );
}

function StackedBarChartBody({
  labels,
  series,
  min,
  max,
  tooltip,
}: ChartModel & { tooltip: (series: string, x: string, y: number) => string }) {
  const groupWidth = plotWidth() / Math.max(1, labels.length);
  const barWidth = Math.max(4, groupWidth * 0.5);
  return (
    <g>
      {labels.map((label, labelIndex) => (
        <StackedBarGroup
          key={label}
          label={label}
          labelIndex={labelIndex}
          series={series}
          min={min}
          max={max}
          groupWidth={groupWidth}
          barWidth={barWidth}
          tooltip={tooltip}
        />
      ))}
    </g>
  );
}

function StackedBarGroup({
  label,
  labelIndex,
  series,
  min,
  max,
  groupWidth,
  barWidth,
  tooltip,
}: {
  label: string;
  labelIndex: number;
  series: SeriesLookup[];
  min: number;
  max: number;
  groupWidth: number;
  barWidth: number;
  tooltip: (series: string, x: string, y: number) => string;
}) {
  let positiveBase = 0;
  let negativeBase = 0;
  const x = CHART_PLOT.left + labelIndex * groupWidth + (groupWidth - barWidth) / 2;
  return (
    <g>
      {series.map((s, seriesIndex) => {
        const value = s.values.get(label) ?? 0;
        const start = value >= 0 ? positiveBase : negativeBase;
        const end = start + value;
        if (value >= 0) positiveBase = end;
        else negativeBase = end;
        const yStart = yFor(start, min, max);
        const yEnd = yFor(end, min, max);
        return (
          <rect
            key={`${s.name}-${label}`}
            x={x}
            y={Math.min(yStart, yEnd)}
            width={barWidth}
            height={Math.max(1, Math.abs(yStart - yEnd))}
            fill={color(seriesIndex)}
            stroke="var(--bg-1)"
            strokeDasharray={dash(seriesIndex)}
          >
            <title>{tooltip(s.name, label, value)}</title>
          </rect>
        );
      })}
    </g>
  );
}

function LineChartBody({
  labels,
  series,
  min,
  max,
  tooltip,
}: ChartModel & { tooltip: (series: string, x: string, y: number) => string }) {
  return (
    <g>
      {series.map((s, seriesIndex) => {
        const points = linePoints(labels, s, min, max);
        return (
          <g key={s.name}>
            {points.length > 1 ? (
              <polyline
                points={points.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke={color(seriesIndex)}
                strokeWidth={2}
                strokeDasharray={dash(seriesIndex)}
              />
            ) : null}
            {points.map((p) => (
              <circle
                key={`${s.name}-${p.label}`}
                cx={p.x}
                cy={p.y}
                r={3}
                fill="var(--bg-1)"
                stroke={color(seriesIndex)}
                strokeWidth={2}
              >
                <title>{tooltip(s.name, p.label, p.value)}</title>
              </circle>
            ))}
          </g>
        );
      })}
    </g>
  );
}

function linePoints(labels: string[], series: SeriesLookup, min: number, max: number) {
  return labels
    .map((label, labelIndex) => {
      const value = series.values.get(label);
      if (value === undefined) return null;
      return { label, value, x: xFor(labelIndex, labels.length), y: yFor(value, min, max) };
    })
    .filter(
      (point): point is { label: string; value: number; x: number; y: number } => point !== null,
    );
}

function color(index: number): string {
  return COLORS[index % COLORS.length]!;
}

function dash(index: number): string {
  return DASHES[index % DASHES.length]!;
}
